// ── Offertmail som .eml-fil ───────────────────────────────────────────────────
// Bygger ett färdigt mail med bilagor som laddas ner och öppnas i Outlook.
// Samma idé som .ics-filen för mötesbokning: webbläsaren kan inte bifoga filer
// åt Outlook via mailto, men en .eml-fil bär med sig både brödtext och bilagor.
//
// Headern X-Unsent: 1 gör att Outlook öppnar filen som ett osänt utkast med
// Skicka-knapp istället för som ett läst mail. Klassiska Outlook för Windows
// respekterar den; nya Outlook och webbversionen öppnar filen som läsvy.

const CRLF = '\r\n';

// Outlook lägger inte på någon signatur i en .eml-fil, så signaturen byggs in
// i brödtexten av anroparen.

function bytesToBase64(bytes) {
  // String.fromCharCode(...bytes) spränger anropsstacken redan vid några hundra
  // kB, så bufferten översätts i portioner.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function utf8ToBase64(str) {
  return bytesToBase64(new TextEncoder().encode(str));
}

// Base64 i ett mail måste radbrytas – Outlook accepterar inte godtyckligt
// långa rader.
function wrap76(b64) {
  return (b64.match(/.{1,76}/g) || []).join(CRLF);
}

// Headers får bara innehålla ASCII. Åäö kodas enligt RFC 2047.
function encodeHeader(value) {
  const str = String(value || '');
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7F]*$/.test(str)) return str;
  return `=?UTF-8?B?${utf8ToBase64(str)}?=`;
}

function encodeAddress({ name, email }) {
  if (!name) return email;
  return `${encodeHeader(name)} <${email}>`;
}

// Brödtexten skrivs som ren text i dialogen men skickas som HTML, så att
// signaturen med logotyp följer med. Tomrader blir styckebrytningar.
export function bodyTextToHtml(text) {
  const esc = s => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const paragraphs = String(text || '').split(/\n{2,}/);
  return paragraphs
    .map(p => `<p style="margin:0 0 14px;font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#000;">${
      esc(p).split('\n').join('<br>')
    }</p>`)
    .join('');
}

// Hämtar en bilaga från Firebase Storage och returnerar den base64-kodad.
// Kräver att bucketen tillåter appens origin i CORS.
export async function fetchAttachment(file) {
  const res = await fetch(file.url);
  if (!res.ok) throw new Error(`${file.name}: ${res.status}`);
  const buffer = await res.arrayBuffer();
  return {
    name: file.name || 'bilaga',
    mimeType: file.mimeType || 'application/octet-stream',
    base64: bytesToBase64(new Uint8Array(buffer)),
  };
}

export function buildEml({ to = [], subject, bodyText, bodyHtml, attachments = [] }) {
  const mixed = `----=_Mixed_${Math.abs(hash(subject + to.length))}`;
  const alt   = `----=_Alt_${Math.abs(hash(subject + bodyText.length))}`;

  const lines = [
    'MIME-Version: 1.0',
    // Utan den här öppnas filen som ett läst mail istället för ett utkast.
    'X-Unsent: 1',
    `To: ${to.map(encodeAddress).join(', ')}`,
    `Subject: ${encodeHeader(subject)}`,
    `Content-Type: multipart/mixed; boundary="${mixed}"`,
    '',
    `--${mixed}`,
    `Content-Type: multipart/alternative; boundary="${alt}"`,
    '',
    `--${alt}`,
    'Content-Type: text/plain; charset="utf-8"',
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(utf8ToBase64(bodyText)),
    '',
    `--${alt}`,
    'Content-Type: text/html; charset="utf-8"',
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(utf8ToBase64(`<html><body>${bodyHtml}</body></html>`)),
    '',
    `--${alt}--`,
    '',
  ];

  attachments.forEach(att => {
    const encodedName = encodeHeader(att.name);
    lines.push(
      `--${mixed}`,
      `Content-Type: ${att.mimeType}; name="${encodedName}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${encodedName}"`,
      '',
      wrap76(att.base64),
      '',
    );
  });

  lines.push(`--${mixed}--`, '');
  return lines.join(CRLF);
}

function hash(str) {
  let h = 0;
  for (let i = 0; i < String(str).length; i += 1) {
    h = ((h << 5) - h + String(str).charCodeAt(i)) | 0;
  }
  return h;
}

export function emlFilename(customerName, date) {
  const safe = String(customerName || 'offert')
    .replace(/[^\wÀ-ÿ -]/g, '').replace(/\s+/g, '-').slice(0, 40);
  return `Offert-${safe || 'kund'}-${date || ''}.eml`;
}

export function downloadEml(emlText, filename) {
  const blob = new Blob([emlText], { type: 'message/rfc822' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
